import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Menu, X, Leaf, Droplet, Sprout } from 'lucide-react';
import './index.css';

function GardeningBusiness() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-emerald-50 to-stone-50">
      {/* Header */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-stone-900">Local Roots</span>
          </div>
          
          <nav className="hidden md:flex gap-8 text-sm font-medium">
            {['Products', 'Why Us', 'Contact'].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(/\s/g, '-')}`} className="text-stone-700 hover:text-emerald-600 transition">
                {item}
              </a>
            ))}
          </nav>

          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden">
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {isMenuOpen && (
          <nav className="md:hidden bg-white border-t">
            {['Products', 'Why Us', 'Contact'].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(/\s/g, '-')}`} className="block px-6 py-3 text-stone-700 hover:bg-emerald-50 border-b">
                {item}
              </a>
            ))}
          </nav>
        )}
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-20 w-96 h-96 bg-amber-200 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6 leading-tight">
            Soil Blends, Fertilizers & <span className="text-emerald-600">Fermented Extracts</span>
          </h1>
          
          <p className="text-xl text-stone-600 mb-4 leading-relaxed">
            Priced for people living paycheck to paycheck.
          </p>
          
          <p className="text-lg text-emerald-700 font-medium mb-8">
            A gallon of fermented Japanese knotweed extract costs $4 to produce. Other companies sell pints for $60. It doesn't need to be that expensive.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button className="px-8 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition transform hover:scale-105">
              Browse Products
            </button>
            <button className="px-8 py-3 border-2 border-stone-900 text-stone-900 rounded-lg font-semibold hover:bg-stone-900 hover:text-white transition">
              Learn More
            </button>
          </div>

          <div className="inline-block bg-white/80 backdrop-blur px-6 py-4 rounded-lg border border-stone-200">
            <p className="text-sm font-medium text-stone-700">
              💡 <strong>Japanese knotweed:</strong> $4 to produce • $16-24/gal (not $60)
            </p>
          </div>
        </div>
      </section>

      {/* The Story */}
      <section id="why-us" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-900 mb-12 text-center">Why We Exist</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Leaf, title: 'Abundant in MA', text: 'There\'s more Japanese knotweed in MA than Dunkin Donuts. It\'s in every yard treated as a weed. It\'s rich in Nitrogen, Potassium, Silica and minerals.' },
              { icon: Droplet, title: 'Real Value', text: 'After fermentation you\'re left with a liquid rich in everything in the plant and beneficial bacterial and fungal strains. Other companies sell that for $60 a pint.' },
              { icon: Sprout, title: 'The Margins Work', text: 'I can help people while making money because of the profit margin. You get affordable products. That\'s the whole point.' }
            ].map((item, i) => (
              <div key={i} className="p-8 border-2 border-emerald-100 rounded-xl hover:border-emerald-400 transition">
                <item.icon className="w-10 h-10 text-emerald-600 mb-4" />
                <h3 className="text-xl font-bold text-stone-900 mb-3">{item.title}</h3>
                <p className="text-stone-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-20 px-6 bg-gradient-to-b from-emerald-50 to-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-900 mb-4 text-center">What We Make</h2>
          <p className="text-center text-stone-600 mb-12 max-w-2xl mx-auto">Soil blends, dry fertilizers, and fermented plant extracts. Most people want smaller quantities, especially online—we offer that.</p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {[
              { title: 'Fermented Plant Extracts', desc: 'Japanese knotweed fermented down. Rich in Nitrogen, Potassium, Silica and minerals. The beneficial bacterial and fungal strains make it work.', price: '$16-24/gal' },
              { title: 'Soil Blends', desc: 'Custom formulations for indoor and outdoor gardens. Built to perform.', price: 'Starting $8/qt' },
              { title: 'Dry Fertilizers', desc: 'Concentrated nutrient blends. No fillers. Mix it yourself or ready to use.', price: 'Starting $12/lb' },
              { title: 'Garden Design & Setup', desc: 'Design and outfit indoor and outdoor gardens from the ground up.', price: 'Consultation' }
            ].map((product, i) => (
              <div key={i} className="bg-white p-8 rounded-xl border-2 border-stone-200 hover:border-emerald-400 hover:shadow-lg transition">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-bold text-stone-900">{product.title}</h3>
                  <span className="text-emerald-600 font-semibold whitespace-nowrap ml-4">{product.price}</span>
                </div>
                <p className="text-stone-600 leading-relaxed">{product.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Numbers */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-900 mb-12 text-center">The Math That Matters</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 p-8 rounded-xl border-l-4 border-emerald-600">
              <p className="text-sm font-semibold text-emerald-700 uppercase mb-2">Market Standard</p>
              <p className="text-4xl font-bold text-stone-900 mb-2">$60<span className="text-xl text-stone-600">/pint</span></p>
              <p className="text-stone-600">What most companies charge for fermented extract</p>
            </div>
            
            <div className="bg-amber-50 p-8 rounded-xl border-l-4 border-amber-600">
              <p className="text-sm font-semibold text-amber-700 uppercase mb-2">Our Price</p>
              <p className="text-4xl font-bold text-stone-900 mb-2">$4-6<span className="text-xl text-stone-600">/pint</span></p>
              <p className="text-stone-600">Healthy margin for us. Better value for you.</p>
            </div>
          </div>

          <div className="mt-12 p-8 bg-stone-100 rounded-xl text-center">
            <p className="text-lg text-stone-700">
              <strong>$4 per gallon is what it costs to make.</strong> Everything else is about getting the product to you at a fair price.
            </p>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-900 mb-6 text-center">Get In Touch</h2>
          
          <p className="text-lg text-stone-600 mb-12 text-center">
            Tell us what products you need and how much. We'll be in touch.
          </p>

          <form 
            action="https://formspree.io/f/YOUR_FORM_ID" 
            method="POST"
            className="bg-stone-50 p-8 rounded-xl border border-stone-200 space-y-6"
          >
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-stone-900 mb-2">Name</label>
              <input 
                type="text" 
                name="name" 
                required
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-emerald-600"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-stone-900 mb-2">Email</label>
              <input 
                type="email" 
                name="email" 
                required
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-emerald-600"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-stone-900 mb-2">Phone</label>
              <input 
                type="tel" 
                name="phone"
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-emerald-600"
              />
            </div>

            {/* Products */}
            <div>
              <label className="block text-sm font-semibold text-stone-900 mb-3">What products are you interested in?</label>
              <div className="space-y-2">
                {['Fermented Plant Extracts', 'Soil Blends', 'Dry Fertilizers', 'Garden Design & Setup'].map(product => (
                  <label key={product} className="flex items-center">
                    <input 
                      type="checkbox" 
                      name="products" 
                      value={product}
                      className="w-4 h-4 accent-emerald-600 rounded"
                    />
                    <span className="ml-3 text-stone-700">{product}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-semibold text-stone-900 mb-2">How much are you looking to order?</label>
              <textarea 
                name="quantity" 
                rows="3"
                placeholder="E.g., 5 gallons of extract, 2 cubic feet of soil blend, etc."
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-emerald-600"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-stone-900 mb-2">Anything else?</label>
              <textarea 
                name="message" 
                rows="3"
                placeholder="Questions, timeline, special requests..."
                className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-emerald-600"
              />
            </div>

            <button 
              type="submit"
              className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition"
            >
              Send Inquiry
            </button>
          </form>

          <div className="mt-12 text-center">
            <p className="text-stone-600 text-sm">Or email directly:</p>
            <a href="mailto:contact@localroots.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">
              contact@localroots.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-100 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Leaf className="w-6 h-6 text-emerald-400" />
                <span className="font-bold text-lg">Local Roots</span>
              </div>
              <p className="text-stone-400 text-sm">Garden products priced for people on real budgets.</p>
            </div>
            
            <div>
              <p className="font-semibold mb-3">Products</p>
              <ul className="text-stone-400 text-sm space-y-2">
                <li>Fermented Extracts</li>
                <li>Soil Blends</li>
                <li>Dry Fertilizers</li>
                <li>Garden Design</li>
              </ul>
            </div>
            
            <div>
              <p className="font-semibold mb-3">Quick Links</p>
              <ul className="text-stone-400 text-sm space-y-2">
                <li><a href="#products" className="hover:text-emerald-400 transition">Products</a></li>
                <li><a href="#why-us" className="hover:text-emerald-400 transition">Why Us</a></li>
                <li><a href="#contact" className="hover:text-emerald-400 transition">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-stone-800 pt-8 text-center text-stone-400 text-sm">
            <p>© 2024 Local Roots. Garden products that don't cost a fortune.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Render React app to DOM
const root = createRoot(document.getElementById('root'));
root.render(<GardeningBusiness />);
